/*
 * How each notification type renders in the inbox and in toasts. Unknown
 * types fall back to the default renderer, so a new backend type shows up
 * sensibly before anyone touches the UI.
 */
import {
  Activity,
  AtSign,
  Bell,
  Bot,
  Building2,
  CalendarClock,
  ClipboardCheck,
  Clock,
  MessageSquare,
  UserPlus,
  type LucideIcon
} from "lucide-react";

import { caseyName } from "@/features/agents/types";

import type { Notification } from "./types";

export type RenderedNotification = {
  title: string;
  description: string | null;
  /** Actor avatar; the type icon is used when absent. */
  avatar: { url?: string | null; name: string } | null;
  /** Label for a button on the inbox row that opens `link`. */
  action?: string;
};

export type NotificationRenderer = {
  icon: LucideIcon;
  /** The small mark on the inbox row's avatar; defaults to the feed kind's. */
  glyph?: LucideIcon;
  /** Announced by its own surface; never toasted. */
  quiet?: boolean;
  render: (n: Notification) => RenderedNotification;
};

const withCount = (title: string, count: number) =>
  count > 1 ? `${title} (${count})` : title;

export const DEFAULT_RENDERER: NotificationRenderer = {
  icon: Bell,
  render: (n) => ({
    title: withCount(n.title, n.count),
    description: n.body || null,
    avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null
  })
};

export const NOTIFICATION_RENDERERS: Record<string, NotificationRenderer> = {
  // The server titles these "<Actor> mentioned you in <case>"; the body is
  // the message preview and `link` deep-links to the thread and message.
  "case_agent.mention": {
    icon: AtSign,
    render: (n) => ({
      title: withCount(n.title, n.count),
      description: n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null
    })
  },
  // Comments on a form's fields. The server titles these "<Actor> mentioned
  // you on I-130 · Part 4 · Line 13.b" or "<Actor> replied on …"; the body is
  // the comment and `link` opens the form at the field with the thread open
  // (`?open=form:<id>&field=<name>&comment=<id>`).
  "form.comment_mention": {
    icon: AtSign,
    glyph: AtSign,
    render: (n) => ({
      title: withCount(n.title, n.count),
      description: n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null,
      action: "Open Comment"
    })
  },
  "form.comment_reply": {
    icon: MessageSquare,
    glyph: MessageSquare,
    render: (n) => ({
      title: withCount(n.title, n.count),
      description: n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null,
      action: "Open Comment"
    })
  },
  // An agent outcome waiting on this person: an approval, a question, a
  // finished or failed turn. The server titles these "<Agent> needs your
  // approval / has a question / finished / couldn't finish in <client>"; the
  // body is the summary and `link` opens the thread. The Agents button,
  // banners and desktop alerts announce these, so no toast; the row is the
  // history, and it's marked read when the signal clears.
  "agent.signal": {
    icon: Bot,
    quiet: true,
    render: (n) => ({
      title: caseyName(n.title),
      description: n.body || null,
      avatar: null
    })
  },
  // Activity on a client the user follows. The server titles these
  // "<Actor> <verb> <subject> in the <case> case"; a burst on the same client
  // collapses into one row, so with `count > 1` the title is the latest event
  // and the description says how many more there were. `link` opens the
  // subject (or the client's profile).
  "client.activity": {
    icon: Activity,
    render: (n) => ({
      title: caseyName(n.title),
      description:
        n.count > 1 ? `${n.count} updates on this client` : n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null
    })
  },
  // The person's own time. The Home evening review and carry-over cards
  // announce the first two, so no toast; `link` opens Home on that day.
  "time.evening_review": {
    icon: Clock,
    quiet: true,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: null
    })
  },
  "time.unreleased_day": {
    icon: Clock,
    quiet: true,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: null
    })
  },
  // A reviewer returned an entry; the body is their note.
  "time.returned": {
    icon: Clock,
    render: (n) => ({
      title: withCount(n.title, n.count),
      description: n.body || null,
      avatar: null
    })
  },
  // An admin nudged the person about unreleased time.
  "time.nudge": {
    icon: Clock,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: null
    })
  },
  // From a company's HR portal: a case request waiting on the firm, a
  // message about an employee, an approval answered. `link` opens the
  // company's request queue or the case.
  "company.request": {
    icon: Building2,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: null
    })
  },
  "company.message": {
    icon: Building2,
    render: (n) => ({
      title: withCount(n.title, n.count),
      description: n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null
    })
  },
  "company.approval_answered": {
    icon: Building2,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null
    })
  },
  // Someone added this person to a client's or a case's team. The server
  // titles these "<Actor> assigned you to <client>" or "... to the <visa> case
  // for <client>"; the body is the due date and the team.
  "client.assigned": {
    icon: UserPlus,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null,
      action: "Open Client"
    })
  },
  "case.assigned": {
    icon: UserPlus,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null,
      action: "Open Case"
    })
  },
  // A calendar entry is coming up. The Notifications sheet already lists it
  // under Coming Due, so the row only toasts; `link` opens the calendar.
  "reminder.due": {
    icon: CalendarClock,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: null,
      action: "Open Calendar"
    })
  },
  // A client submitted (or revised) an intake. The client is the actor; the
  // body is the answer and flag counts and `link` opens the submission for
  // review. The row is marked read for everyone once someone reviews it.
  "intake.submitted": {
    icon: ClipboardCheck,
    render: (n) => ({
      title: n.title,
      description: n.body || null,
      avatar: n.actor ? { url: n.actor.avatar_url, name: n.actor.name } : null,
      action: n.context.revision === true ? "Review Changes" : "Review Intake"
    })
  }
};

export const rendererFor = (type: string): NotificationRenderer =>
  NOTIFICATION_RENDERERS[type] ?? DEFAULT_RENDERER;

export const renderNotification = (n: Notification): RenderedNotification =>
  rendererFor(n.type).render(n);

/** Register a renderer for a type (features call this at module load). */
export const registerNotificationRenderer = (
  type: string,
  renderer: NotificationRenderer
): void => {
  NOTIFICATION_RENDERERS[type] = renderer;
};
